/**
 * @file     capabilities.js
 *           Functions to support requirements/capabilities check.
 *
 * @author   Ryan Saweczko, ryansaweczko@distributive.network
 * @date     Jan 2025
 */
'use strict';

const { capabilities } = require('dcp/common/scheduler-constants')

/**
 * Calculates the need/has capability bits for a given job/worker's capability object.
 *
 * @param {object} caps - the object describing a capabilities
 * @returns {number} - the capabilities described as a series of bits
 */
exports.toBits = function toBits(caps)
{
  let mask = 0;
  for (let prop in capabilities)
  {
    if (typeof capabilities[prop] === 'number' && caps[prop])
      mask += capabilities[prop];
    else if (typeof capabilities[prop] === 'object')
      mask += subToBits(caps[prop], capabilities[prop]);
  }
  return mask;
}

function subToBits(caps, subCapabilities)
{
  let mask = 0;
  if (!caps)
    return mask;
  for (let prop in subCapabilities)
  {
    if (typeof subCapabilities[prop] === 'number' && caps[prop])
      mask += subCapabilities[prop];
    else if (typeof subCapabilities[prop] === 'object')
      mask += subToBits(caps[prop], subCapabilities[prop]);
  }
  return mask;
}

/**
 * Calculates the requirements object from the needsCapability bits for a given job
 *
 * @param {number} caps - the capability bits
 * @param {object} requirements - part of the capabilities object ot build up the requirements - recursive use
 * @returns {object} - the capabilities object
 */
exports.fromBits = function fromBits(caps, requirements)
{
  const ret = {};
  const capObj = requirements || capabilities;
  for (let prop in capObj)
  {
    if (typeof capObj[prop] === 'object')
      ret[prop] = fromBits(caps, capObj[prop]);
    else if (caps & capObj[prop])
      ret[prop] = true;
    else
      ret[prop] = false;
  }
  return ret;
}
